// module imports
import { semicolonDelimited2Object } from 'modules/Helpers'
// constant imports
import { CONTACTS_MAP } from 'constants/Contacts'
import {
  REASON,
  CUSTOM_HEADER,
  SHOW_MISSED_HEADER,
  HEADER_ENTITIES,
  ENTITIES_MAP,
} from 'constants/Sessions'

export const forceNet2phoneCodecs = (originalSdp) => {
  let sdp = originalSdp
  // -- removing crypto lines from sdp
  sdp.replace(/a=crypto([\s\S]*?)\n/, '')

  // --- splitting sdp into lines
  const sdpLines = sdp.split('\n')

  // --- matching m=audio lines
  const audioLines = sdpLines.filter((lines) => lines.includes('m=audio'))

  // --- matching telephone-event lines
  const telephoneEvents = sdpLines.filter((lines) =>
    lines.includes('telephone-event'),
  )

  // --- return early if we don't have sdp lines to work with.
  if (telephoneEvents.length <= 1) {
    return sdp
  }

  // --- identify g711 telephone-event line
  const telephoneEvent8000 = telephoneEvents.filter((event) =>
    event.includes('telephone-event/8000'),
  )[0]

  // --- get telephone-event id
  const telephoneEventId = telephoneEvent8000
    .match(/a=rtpmap:([\s\S]*?) /)[0]
    .split(':')[1]

  // --- enforce the only telephone-event net2phone supports...
  const n2pTelephoneEvent = telephoneEvent8000.replace(telephoneEventId, '101 ')

  // --- getting Id's of unnecessary telephone-event lines
  const unnecessaryIds = []
  telephoneEvents.forEach((event) => {
    unnecessaryIds.push(event.match(/a=rtpmap:([\s\S]*?) /)[0].split(':')[1])

    // --- removing unnecessary telephone-event lines
    if (event !== telephoneEvent8000) {
      sdp = sdp.replace(`${event}\n`, '')
    } else {
      // --- add n2pTelephoneEvent line
      sdp = sdp.replace(`${event}\n`, `${n2pTelephoneEvent}\n`)
    }
  })

  // --- update m=audio line
  unnecessaryIds.forEach((ID, index) => {
    let id = ID
    if (index === unnecessaryIds.length - 1) {
      id = id.trim()
    }
    audioLines[0] = audioLines[0].replace(id, '')
  })
  audioLines[0] = `${audioLines[0].trim()} 101`
  sdp = sdp.replace(/m=audio([\s\S]*?)\n/, `${audioLines[0]}\n`)

  return sdp
}

const reasonHeader = (data) => {
  // find reason header
  const rawHeader = data?.headers?.[REASON.header]?.find((reason) =>
    reason.raw.toString().startsWith(REASON.headerKey),
  )?.raw
  // parse header
  return semicolonDelimited2Object(rawHeader || '')
}

const reasonOK = (data) => {
  // parse header
  const reason = reasonHeader(data)
  // check if answered elsewhere
  return reason?.[REASON.key] === REASON.value
}

const customHeaders = (data) => {
  // validate custom header exists
  const rawHeader = data?.headers?.[CUSTOM_HEADER]?.[0]?.raw

  if (!rawHeader) {
    return {
      [SHOW_MISSED_HEADER]: true,
    }
  }

  const headers = semicolonDelimited2Object(rawHeader)

  // convert to boolean
  if (headers[SHOW_MISSED_HEADER]) {
    headers[SHOW_MISSED_HEADER] = !!headers[SHOW_MISSED_HEADER]
  }

  return headers
}

export const isParkedCall = (data) => {
  // get custom headers
  const reason = reasonHeader(data)

  let slot = ''

  // no reason header
  if (!reason) {
    return slot
  }

  // check reason is ok and was park event
  const ok = reason[REASON.key] === REASON.value
  const parked = reason[REASON.parked.key] === REASON.parked.value

  // return slot number or ''
  if (ok && parked) {
    slot = reason[REASON.parked.slotKey] || ''
  }

  return slot
}

/**
 * Is the call actually a missed call.
 * Should return false if cancel status is 200 or the custom missed header is here
 *
 * @param data
 * @returns {boolean}
 */
export const isMissedCall = (data) =>
  !(reasonOK(data) && customHeaders(data)[SHOW_MISSED_HEADER])

export const entityFromHeaders = (data) => {
  // get custom headers
  const headers = customHeaders(data)

  // find known entities in headers
  const entities = Object.keys(headers)
    .map((type) =>
      type && HEADER_ENTITIES.includes(type)
        ? { type: ENTITIES_MAP[type] || type, name: headers[type] }
        : undefined,
    )
    .filter(Boolean)

  // map to valid name
  let entity = entities?.[0]
  if (entities.length > 1) {
    entity =
      entities.filter((ent) => ent.type !== CONTACTS_MAP.teamMember)[0] ||
      entity
  }

  // return first one
  return entity
}
